import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { ARTICLE_BY_ID, TIPS } from "./content";
import { useHelp } from "./HelpDrawer";

/**
 * An in-page tip (ALN-53): the (i) next to a label, from docs/help/tips.md (keyed by the label's
 * exact text). Selecting it shows the tip in a small popover with "Learn more", which opens the
 * linked article in the help drawer. Esc or selecting elsewhere closes it; Esc returns focus.
 * Renders nothing for a label with no tip.
 */
export function Tip({ term }: { term: string }) {
  const tip = TIPS[term];
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const popId = useId();
  const { openHelp } = useHelp();

  // Place the popover under the button, inside the viewport (above it when there's no room below).
  useLayoutEffect(() => {
    const p = pop.current, b = btn.current;
    if (!open || !p || !b) return;
    const r = b.getBoundingClientRect();
    const w = p.offsetWidth, h = p.offsetHeight;
    const left = Math.min(Math.max(16, r.left - 12), window.innerWidth - w - 16);
    let top = r.bottom + 8;
    if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 8);
    p.style.left = `${left}px`;
    p.style.top = `${top}px`;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Capture phase: Esc closes the tip before anything else (the help drawer) sees it.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      btn.current?.focus();
    };
    const away = (e: Event) => {
      const t = e.target as Node;
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) setOpen(false);
    };
    const shut = () => setOpen(false);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", away);
    window.addEventListener("scroll", shut, true);
    window.addEventListener("resize", shut);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", away);
      window.removeEventListener("scroll", shut, true);
      window.removeEventListener("resize", shut);
    };
  }, [open]);

  if (!tip) return null;
  const article = ARTICLE_BY_ID[tip.article];
  return (
    <>
      <button ref={btn} type="button" className="tip" aria-label={`About ${term}`} aria-expanded={open} aria-controls={open ? popId : undefined} onClick={() => setOpen(!open)}>i</button>
      {open && (
        <div ref={pop} className="tippop" id={popId} role="dialog" aria-modal="false" aria-label={term}>
          <b>{term}</b>
          <span>{tip.text}</span>
          {article && (
            <div>
              <button type="button" className="linkbtn" onClick={() => { setOpen(false); btn.current?.focus(); openHelp(article.id); }}>Learn more: {article.title}</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
